import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import { useIntl } from 'react-intl';

import { getQualityMetricsData } from 'services/business';
import { formatMoney } from 'utils/wallet';

import BRTag from 'components/BRTag/BRTag';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import { notify } from 'components/Notify/Notify';
import MerchantInfoCard from '../cards/MerchantInfoCard/MerchantInfoCard';
import StatCard from '../StatCard/StatCard';

import { ReactComponent as PackageIcon } from 'assets/bosta-icons/package-check.svg';
import { ReactComponent as ArrowIcon } from 'assets/bosta-icons/Arrow-up.svg';
import { ReactComponent as InfoIcon } from 'assets/bosta-icons/Info-Circle.svg';

import './QualityMetrics.less';

const QualityMetrics = ({ businessId, dateValue, ...props }) => {
  const { formatMessage } = useIntl();

  const [isLoading, setIsLoading] = useState(false);
  const [growthData, setGrowthData] = useState({
    revenueGrowth: null,
    ordersGrowth: null,
    currentMonthOrdersCount: null,
    currentMonthRevenue: null
  });

  useEffect(() => {
    if (!businessId || !dateValue) {
      return;
    }

    const fetchWalletCreditsData = async () => {
      setIsLoading(true);
      const queryParams = {
        businessId,
        date: dateValue?.format('YYYY-MM-DD')
      };
      try {
        const {
          data: {
            revenueGrowth,
            ordersGrowth,
            currentMonthOrdersCount,
            currentMonthRevenue
          }
        } = await getQualityMetricsData(queryParams);

        setGrowthData({
          revenueGrowth,
          ordersGrowth,
          currentMonthOrdersCount,
          currentMonthRevenue
        });
      } catch (error) {
        notify(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWalletCreditsData();
  }, [businessId, dateValue]);

  const growthPercentage = (percentageValue) => {
    if (typeof percentageValue !== 'number') return null;

    const color =
      percentageValue > 0 ? 'green' : percentageValue < 0 ? 'red' : 'gray';
    const messageId =
      percentageValue > 0
        ? 'merchant_analysis.quality_metrics.last_month_up'
        : 'merchant_analysis.quality_metrics.last_month_down';

    return (
      <BRTag color={color}>
        <div
          className={classnames(
            'br-quality-metrics__content-value__percentage',
            {
              down_value: percentageValue < 0
            }
          )}
        >
          <ArrowIcon />
          <span className="caption_md">
            {formatMessage(
              { id: messageId },
              { value: Math.abs(percentageValue) }
            )}
          </span>
        </div>
      </BRTag>
    );
  };

  return (
    <LoadingWrapper loading={isLoading}>
      <StatCard
        title={formatMessage({ id: 'merchant_analysis.quality_metrics.title' })}
        titleIcon={<PackageIcon className="br-financial-metrics-icon" />}
        className="br-quality-metrics"
      >
        <div className="br-quality-metrics__content">
          <MerchantInfoCard
            label={formatMessage({
              id: 'merchant_analysis.quality_metrics.revenue_growth'
            })}
            labelTooltip={
              <>
                <Tooltip
                  title={formatMessage({
                    id: 'merchant_analysis.quality_metrics.revenue_tooltip'
                  })}
                >
                  <InfoIcon />
                </Tooltip>
              </>
            }
            value={
              <div className="br-quality-metrics__content-value">
                <span>{formatMoney(growthData?.currentMonthRevenue)}</span>
                {growthPercentage(growthData?.revenueGrowth)}
              </div>
            }
          />

          <MerchantInfoCard
            label={formatMessage({
              id: 'merchant_analysis.quality_metrics.orders_growth'
            })}
            labelTooltip={
              <Tooltip
                title={formatMessage({
                  id: 'merchant_analysis.quality_metrics.orders_tooltip'
                })}
              >
                <InfoIcon />
              </Tooltip>
            }
            value={
              <div className="br-quality-metrics__content-value">
                <span>{growthData?.currentMonthOrdersCount}</span>
                {growthPercentage(growthData?.ordersGrowth)}
              </div>
            }
          />
        </div>
      </StatCard>
    </LoadingWrapper>
  );
};

export default QualityMetrics;
